// Photographs used on the site, with the credit the CDC Public Health Image Library
// (PHIL) asks for. Every entry was checked against its PHIL details page: the ID, the
// content provider and photographer, and the "public domain" status. PHIL asks that
// reuse credit CDC and, where known, the photographer, and name the CDC site as the
// free source, so captions use `credit` and link `detailsUrl`.
//
// No public-domain PHIL photograph of a Lone Star tick larva could be found, so the
// identification plate starts at the nymph and the larva appears only in the drawn,
// to-scale figure.

export interface PhilPhoto {
  /** File under /public. */
  src: string;
  philId: number;
  /** PHIL's content provider and photographer, as PHIL lists them. */
  provider: string;
  photographer: string;
  width: number;
  height: number;
  alt: string;
}

export const PHIL_DETAILS = (id: number) => `https://phil.cdc.gov/Details.aspx?pid=${id}`;

/** One credit line for a set of PHIL photos by the same photographer. */
export function philCredit(photos: readonly PhilPhoto[]): string {
  const photographers = [...new Set(photos.map((p) => p.photographer))];
  const ids = photos.map((p) => `#${p.philId}`);
  const idList = ids.length > 1 ? `${ids.slice(0, -1).join(", ")} and ${ids.at(-1)}` : ids[0];
  return `Photos: ${photographers.join(", ")}, CDC. Public Health Image Library (phil.cdc.gov) ${idList}. Public domain.`;
}

const LOFTIS_SERIES =
  "CDC/ Dr. Amanda Loftis, Dr. William Nicholson, Dr. Will Reeves, Dr. Chris Paddock";

/** The adult female on a grass blade, used on the home page. PHIL #4407. */
export const FEMALE_ON_GRASS: PhilPhoto = {
  src: "/images/lone-star-tick-female-cdc-phil-4407.jpg",
  philId: 4407,
  provider: "CDC/ Michael L. Levin, Ph. D.",
  photographer: "James Gathany",
  width: 575,
  height: 546,
  alt: "An adult female Lone Star tick seen from above on a green leaf: a reddish-brown oval body with a single cream-coloured spot in the middle of its back, eight banded legs and long mouthparts at the front.",
};

export type PlateStage = "nymph" | "male" | "female" | "fed-female";

/**
 * Four stages from one 2006 studio series, all seen from above on black, so they read
 * as a set. Cropped square around the tick; not to scale with each other.
 */
export const LIFE_STAGE_PHOTOS: readonly (PhilPhoto & { stage: PlateStage; label: string })[] = [
  {
    stage: "nymph",
    label: "Nymph",
    src: "/images/lone-star-tick-nymph-cdc-phil-9535.jpg",
    philId: 9535,
    provider: LOFTIS_SERIES,
    photographer: "James Gathany",
    width: 520,
    height: 520,
    alt: "A Lone Star tick nymph seen from above: a pale, rounded, tan body with a darker brown shield at the front, eight legs and no white spot.",
  },
  {
    stage: "male",
    label: "Adult male",
    src: "/images/lone-star-tick-male-cdc-phil-8684.jpg",
    philId: 8684,
    provider: LOFTIS_SERIES,
    photographer: "James Gathany",
    width: 520,
    height: 520,
    alt: "An adult male Lone Star tick seen from above: an oval reddish-brown body with faint pale streaks around the rear edge, eight long legs and no single white spot.",
  },
  {
    stage: "female",
    label: "Adult female",
    src: "/images/lone-star-tick-female-cdc-phil-8682.jpg",
    philId: 8682,
    provider: LOFTIS_SERIES,
    photographer: "James Gathany",
    width: 520,
    height: 520,
    alt: "An adult female Lone Star tick seen from above: a reddish-brown body with one bright cream-coloured spot in the middle of the shield behind the head.",
  },
  {
    stage: "fed-female",
    label: "Fed female",
    src: "/images/lone-star-tick-fed-female-cdc-phil-8679.jpg",
    philId: 8679,
    provider: LOFTIS_SERIES,
    photographer: "James Gathany",
    width: 520,
    height: 520,
    alt: "A Lone Star tick female swollen after feeding, seen from above: a large, smooth, grey-white oval body with mottled darker patches, the small head and legs barely visible at the front.",
  },
];
